import { useMemo, useState } from 'react';

import { timePlannerHasStaffOnlyItems } from '../../../../components/TimePlanner/timePlannerUtils';
import { formatDateOnlyISO } from '../../../../utils/dateUtil';
import { getDefaultTemplateDate } from '../../../../utils/apptTemplateUtils';
import { useSaveTimePlanning } from './useSaveTimePlanning';
import { useTimePlanningWeekDays } from './useTimePlanningWeekDays';
import { useDefaultApptTemplate } from '../../../../hooks/apptTemplate/useDefaultApptTemplate';
import { useCubicles } from '../../../../query/cubicles';

const weekDaysWithSundayAsLastDay = (weekDays: number[] | undefined) => {
  /**
   * We want first day of the week, but 0 is sunday...
   */
  return (weekDays || [0]).map((day) => (day === 0 ? 7 : day));
};

const sundayLastDayAsFirstDay = (day: number) => {
  return day === 7 ? 0 : day;
};

export const useSiteFirstDefaultTemplateDate = () => {
  const weekDays = useTimePlanningWeekDays();

  return useMemo(() => {
    const firstDay = Math.min(...weekDaysWithSundayAsLastDay(weekDays));
    return getDefaultTemplateDate(sundayLastDayAsFirstDay(firstDay));
  }, [weekDays]);
};

export const useSiteLastDefaultTemplateDate = () => {
  const weekDays = useTimePlanningWeekDays();

  return useMemo(() => {
    const lastDay = Math.max(...weekDaysWithSundayAsLastDay(weekDays));
    return getDefaultTemplateDate(sundayLastDayAsFirstDay(lastDay));
  }, [weekDays]);
};

export const useDefaultTimePlanning = ({
  selectedDate,
}: {
  selectedDate: Date;
}) => {
  const [showOnline, setShowOnline] = useState(false);

  const { isLoading: isTemplatesLoading, defaultTemplate } =
    useDefaultApptTemplate({
      selectedDate,
    });

  const { isLoading: isCreateOrUpdateLoading, saveTimePlanning } =
    useSaveTimePlanning({
      defaultTemplate,
      selectedDateOnlyIso: formatDateOnlyISO(selectedDate),
    });

  const { data, isLoading: isCubiclesLoading } = useCubicles();
  const cubicleCount = data?.data?.data.length;

  const showOnlineMustBeShown = timePlannerHasStaffOnlyItems(defaultTemplate);

  return {
    showOnline,
    setShowOnline,
    saveTimePlanning,
    defaultTemplate,
    cubicleCount,
    showOnlineMustBeShown,
    isLoading:
      isTemplatesLoading || isCreateOrUpdateLoading || isCubiclesLoading,
  };
};
